import { A } from '@adornis/base/env-info.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo';
import { DateTime } from 'luxon';
import { Draft } from '../db/Draft.js';

export const createDraft = registerQuery({
  type: () => Draft,
  operationName: 'createDraft',
  resolve: (draftClassName: string, referenceClassName: string, referenceID: string, draftTemplate?: Draft) => {
    return async (gqlFields: BaseQLSelectionSet<Draft>) => {
      // resolve collection
      const resolvedNode = baseqlMetaData.entities.resolveNode(referenceClassName);
      const OriginalClass = resolvedNode.type() as typeof MongoEntity;

      // check for original entity to be existent
      const rawEntityCollection = await getRawCollection<MongoEntity>(OriginalClass._collectionName);
      let original = await rawEntityCollection.findOne<MongoEntity>({ _id: referenceID });
      if (!original)
        throw new Error(`no entity with id '${referenceID}' in collection '${OriginalClass._collectionName}' found.`);

      const DraftClass = baseqlMetaData.entities.resolveNode(draftClassName).type() as typeof Draft;

      // create draft
      const draft = new DraftClass({
        ...(draftTemplate ? draftTemplate.toObject() : {}),
        _id: A.getGloballyUniqueID(),
      });
      original = constructValue(original)!;

      draft.referenceID = original._id;
      const copy = new OriginalClass({ ...original.toObject(), _id: null }) as MongoEntity;
      draft.entity = copy;

      const rawDraftCollection = await getRawCollection<Draft>(DraftClass._collectionName);

      // before create
      draft.createdAt = DateTime.now();
      const userID = (await CurrentUserInfo.getCurrentUserInfo()({ userID: 1 })).userID;
      if (!userID) throw new Error("no user on CurrentUserInfo is set. Draft can't be generated");
      draft.createdBy = userID;
      // create
      const insertResult = await rawDraftCollection.insertOne(draft.toObject() as any as Draft);
      return await getByID(Draft, insertResult.insertedId)(gqlFields);
    };
  },
  params: [
    { name: 'draftClassName', type: () => String },
    { name: 'referenceClassName', type: () => String },
    { name: 'referenceID', type: () => String },
    { name: 'draftTemplate', type: () => Draft },
  ],
});
